import {useState} from "react";
import {Alert, Table, Tag} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {useGetGscCannibalizationQuery} from "~/reduxs/api/gscApiSlice";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);
const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/** 'YYYY-MM-DD HH:MM:SS' → 'dd/mm/yyyy HH:MM' (parse tay, không lệch múi giờ). */
function fmtDate(s) {
	if (!s) return '—';
	const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(s));
	return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : String(s);
}

/** Bảng con: các trang cùng đua 1 từ khoá (theo clicks giảm dần). */
function pagesTable(pages) {
	const cols = [
		{title: 'Trang', dataIndex: 'page', key: 'page', ellipsis: true,
			render: (v) => <a href={v} target="_blank" rel="noreferrer">{v}</a>},
		{title: 'Clicks', dataIndex: 'clicks', key: 'clicks', width: 90, align: 'right', render: fmt},
		{title: 'Impressions', dataIndex: 'impressions', key: 'impressions', width: 120, align: 'right', render: fmt},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', width: 80, align: 'right', render: (v) => Number(v).toFixed(1)},
	];
	return <Table rowKey="page" size="small" columns={cols} dataSource={pages} pagination={false} />;
}

/**
 * Tab "Cannibalization" — từ khoá có NHIỀU URL cùng đua (Google phân vân chọn trang nào →
 * loãng thứ hạng). Snapshot gộp cửa sổ `window_days` ngày, phục vụ từ DB (bảng nhỏ, xoá-rồi-ghi);
 * nút "Đồng bộ" ép BE gọi lại Google. Mở rộng mỗi dòng để xem các trang đang đua.
 */
function GscCannibalization({projectId}) {

	const [syncTick, setSyncTick] = useState(0);

	const {data = {rows: [], synced_at: null, stale: false}, isFetching} =
		useGetGscCannibalizationQuery(syncTick ? {projectId, refresh: syncTick} : {projectId});

	const rows = data.rows || [];

	const columns = [
		{title: 'Từ khoá', dataIndex: 'query', key: 'query', ellipsis: true},
		{title: 'Số trang đua', dataIndex: 'page_count', key: 'page_count', width: 130, align: 'center',
			sorter: (a, b) => a.page_count - b.page_count, defaultSortOrder: 'descend',
			render: (v) => <Tag color={v >= 3 ? 'red' : 'orange'}>{v} trang</Tag>},
		{title: 'Tổng clicks', dataIndex: 'clicks', key: 'clicks', width: 120, align: 'right',
			sorter: (a, b) => a.clicks - b.clicks, render: fmt},
		{title: 'Tổng impressions', dataIndex: 'impressions', key: 'impressions', width: 150, align: 'right',
			sorter: (a, b) => a.impressions - b.impressions, render: fmt},
	];

	return (
		<div>
			<div className={cn('rangeBar')}>
				<div className={cn('metaRow')} style={{margin: 0}}>
					<FontAwesomeIcon icon="fa-light fa-code-branch" />
					<span>Từ khoá có ≥2 URL cùng xếp hạng ({data.window_days || 90} ngày gần nhất).</span>
					{data.synced_at && <span>Đồng bộ lúc {fmtDate(data.synced_at)}</span>}
				</div>
				<Button white small loading={isFetching} onClick={() => setSyncTick(Date.now())}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-rotate" />}>Đồng bộ</Button>
			</div>

			<p className={cn('metaRow')}>
				Nhiều trang cùng đua một từ khoá khiến Google phân vân chọn trang nào → loãng thứ hạng. Nên gộp nội
				dung, đặt canonical, hoặc phân biệt intent giữa các trang.
			</p>

			{data.stale && (
				<Alert type="warning" showIcon style={{marginBottom: 12}}
					message="Không cập nhật được từ Google, đang hiển thị dữ liệu đã lưu."
					description={data.message || undefined} />
			)}

			<Table
				rowKey="query"
				size="small"
				loading={isFetching}
				columns={columns}
				dataSource={rows}
				expandable={{expandedRowRender: (r) => pagesTable(r.pages || [])}}
				pagination={{pageSize: 15, hideOnSinglePage: true, showSizeChanger: false}}
				locale={{emptyText: 'Không phát hiện từ khoá nào bị nhiều trang đua trong cửa sổ này.'}}
			/>
		</div>
	);
}

export default GscCannibalization;
